import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAssetsFromCurrentFolder,
  selectEditingAssetId,
  selectSelectedFolder,
  selectSortBy,
  setEditingAsset,
} from "../../app/slices/AssetsSlice";
import {
  BrowseDialogViewMode,
  selectBrowseDialogViewMode,
  setBrowseDialogViewMode,
} from "../../app/slices/GlobalSlice";
import PiBrowseLayout from "../../components/PiBrowseLayout";
import {
  browseListboxId,
  browseOptionId,
} from "../../components/browseListboxIds";
import { AssetDetailsDTO, AssetKey } from "../../dto/AssetDTO";
import { useFileExplorerLoading } from "../../hooks/FileExplorerHooks";
import useFilterableList from "../../hooks/useFilterableList";
import { GridDirection } from "../interaction_picker/navigateGroupedGrid";
import ContextualHint, { HintId } from "../onboarding/hints/ContextualHint";
import AssetDetailsPane from "./AssetDetailsPane";
import AssetResults, { DEFAULT_ASSET_COLUMNS } from "./AssetResults";
import AssetScopeNotice from "./AssetScopeNotice";
import EditAssetDialog from "./EditAssetDialog";
import FileExplorerMultiSelectToggle from "./FileExplorerMultiSelectToggle";
import FileExplorerSortDropdown from "./FileExplorerSortDropdown";
import FolderView from "./FolderView";
import { navigateAssetGrid } from "./navigateAssetGrid";
import { useRenameAsset } from "./useRenameAsset";

interface FileExplorerBodyProps {
  onClose: () => void;
  type?: AssetKey;
  onSelect?: (asset: AssetDetailsDTO) => void;
  allowMultiSelect?: boolean;
  onMultiSelect?: (assets: AssetDetailsDTO[]) => void;
}

const EMPTY_SEARCH_STRINGS: string[] = [];
const getSearchStrings = () => EMPTY_SEARCH_STRINGS;

const DIALOG = "fileexplorer";

/**
 * Everything inside the file explorer dialog. Search is server-backed (the
 * filtered endpoint returns flat, cross-folder results, which is why the rail
 * hides and the drop zone disappears while a query is set). The keyboard
 * highlight indexes assets; the drop zone occupies cell 0 but is never
 * navigable.
 */
export default function FileExplorerBody(props: FileExplorerBodyProps) {
  const { onClose, type, allowMultiSelect, onMultiSelect } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [filter, setFilter] = useState("");
  const [multiSelect, setMultiSelect] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<AssetDetailsDTO[]>([]);
  const [tileColumns, setTileColumns] = useState(DEFAULT_ASSET_COLUMNS);
  const [renamingAssetId, setRenamingAssetId] = useState<string>();

  const assets = useSelector(selectAssetsFromCurrentFolder);
  const selectedFolder = useSelector(selectSelectedFolder);
  const sortBy = useSelector(selectSortBy);
  const viewMode = useSelector(selectBrowseDialogViewMode);
  const editingAssetId = useSelector(selectEditingAssetId);

  const { refreshFolder, fetchFolders, onRerender } = useFileExplorerLoading();
  const renameAsset = useRenameAsset();

  useEffect(() => {
    fetchFolders(); // only fetches folders if not already fetched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deliberately NOT scoped by `type`. The endpoint can filter by asset key,
  // but the notice tells the user other files stay visible so they can still
  // be uploaded and organised — and that was a lie while the server filtered
  // them out. The type scope is a UI concern now: it dims and blocks, it does
  // not hide.
  useEffect(() => {
    refreshFolder(undefined, filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder, filter, sortBy]);

  const showDropzone = !filter && !!selectedFolder;
  const offset = showDropzone ? 1 : 0;

  // Opened from the dashboard menu the explorer is a file manager, not a
  // picker: nothing consumes a selection, so there is nothing to confirm.
  // Keyed on the callbacks rather than on `type`, so a "pick any file type"
  // caller still gets its confirm.
  const canPick = !!props.onSelect || (!!allowMultiSelect && !!onMultiSelect);

  const isBlocked = useCallback(
    (asset: AssetDetailsDTO | undefined) =>
      !asset ||
      asset.key === AssetKey.Unsupported ||
      asset.key === AssetKey.Loading ||
      (type !== undefined && asset.key !== type),
    [type]
  );

  const isAssetChecked = useCallback(
    (asset: AssetDetailsDTO) =>
      selectedAssets.find((a) => a.assetId === asset.assetId) !== undefined,
    [selectedAssets]
  );

  const toggleAsset = useCallback(
    (asset: AssetDetailsDTO) => {
      if (isBlocked(asset)) return;
      setSelectedAssets((current) =>
        current.find((a) => a.assetId === asset.assetId)
          ? current.filter((a) => a.assetId !== asset.assetId)
          : [...current, asset]
      );
    },
    [isBlocked]
  );

  const confirmSingle = useCallback(
    (asset: AssetDetailsDTO) => {
      if (!props.onSelect || isBlocked(asset)) return;
      try {
        props.onSelect(asset);
      } finally {
        onClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBlocked, onClose, props.onSelect]
  );

  const confirmMulti = useCallback(() => {
    if (selectedAssets.length === 0) return;
    try {
      onMultiSelect?.(selectedAssets);
    } finally {
      onClose();
    }
  }, [selectedAssets, onMultiSelect, onClose]);

  const moveHighlight = useCallback(
    (index: number, direction: GridDirection) =>
      navigateAssetGrid(
        assets.length,
        viewMode === "tile" ? tileColumns : 1,
        offset,
        index,
        direction
      ),
    [assets.length, viewMode, tileColumns, offset]
  );

  const list = useFilterableList<AssetDetailsDTO>({
    items: assets,
    getSearchStrings,
    moveHighlight,
    initiallyOpen: true,
    focusSearchOnSlash: true,
    onSelect: (asset) => {
      // Enter. The confirm owns the close, so the hook's close is vetoed —
      // and in browse mode there is nothing to confirm, so it does nothing.
      if (!canPick) return false;
      if (multiSelect) confirmMulti();
      else if (asset && !isBlocked(asset)) confirmSingle(asset);
      return false;
    },
    onSpace: multiSelect ? toggleAsset : undefined,
    onClose,
    // The hook captures Enter, Escape and the arrows on `window`, so it has
    // to stand down whenever something on top of the explorer owns the
    // keyboard: the inline rename field, or the localize dialog. Otherwise
    // Escape closes both dialogs at once and the arrows move a highlight
    // nobody can see.
    suspended: !!renamingAssetId || !!editingAssetId,
  });

  const { selectedIndex, setSelectedIndex } = list;
  const highlighted = assets[selectedIndex];

  const onSearchChange = useCallback(
    (value: string) => {
      setFilter(value);
      setSelectedIndex(0);
    },
    [setSelectedIndex]
  );

  /**
   * Now that a scoped explorer lists every file, the first one is often not
   * pickable — which would open the picker with a dead primary button and an
   * empty details pane. Land on the first asset that CAN be picked instead.
   *
   * Once per folder/query only: after that the highlight belongs to the user,
   * who is free to move onto blocked rows to reach their menus.
   */
  const landedRef = useRef(false);
  useEffect(() => {
    landedRef.current = false;
  }, [selectedFolder, filter, type]);

  useEffect(() => {
    if (landedRef.current || type === undefined || assets.length === 0) return;
    landedRef.current = true;

    // Sparse while pages are still loading; `isBlocked(undefined)` is true, so
    // holes are skipped rather than landed on.
    const firstPickable = assets.findIndex((asset) => !isBlocked(asset));
    if (firstPickable > 0) setSelectedIndex(firstPickable);
  }, [assets, type, isBlocked, setSelectedIndex, selectedFolder, filter]);

  const onViewModeChange = useCallback(
    (mode: BrowseDialogViewMode) => {
      dispatch(setBrowseDialogViewMode(mode));
    },
    [dispatch]
  );

  const onSelectIndex = useCallback(
    (index: number) => {
      setSelectedIndex(index);
      const asset = assets[index];
      if (multiSelect && asset) toggleAsset(asset);
    },
    [setSelectedIndex, assets, multiSelect, toggleAsset]
  );

  const onConfirmIndex = useCallback(
    (index: number) => {
      const asset = assets[index];
      if (!asset) return;
      setSelectedIndex(index);
      if (multiSelect) toggleAsset(asset);
      else confirmSingle(asset);
    },
    [assets, multiSelect, toggleAsset, confirmSingle, setSelectedIndex]
  );

  const primaryLabel = !canPick
    ? undefined
    : multiSelect
      ? t("file_explorer_ui.add_files", { count: selectedAssets.length })
      : t("file_explorer_ui.use_file");
  const primaryDisabled = multiSelect
    ? selectedAssets.length === 0
    : isBlocked(highlighted);

  return (
    <PiBrowseLayout
      searchValue={filter}
      onSearchChange={onSearchChange}
      searchPlaceholder={t("searchFiles")}
      searchInputRef={list.searchInputRef}
      searchFieldId="onboarding-fileexplorer-searchFiles"
      dataTestId="file-explorer-dialog"
      searchTestId="file-explorer-search-input"
      listboxId={browseListboxId(DIALOG)}
      activeDescendantId={browseOptionId(DIALOG, selectedIndex)}
      scopeSwitcher={
        <div className="flex items-center gap-[8px]">
          {/* Hugs its label, as the frames draw it. */}
          <div data-testid="file-explorer-sort-dropdown">
            <FileExplorerSortDropdown />
          </div>
          {allowMultiSelect && (
            <FileExplorerMultiSelectToggle
              multiSelect={multiSelect}
              selectedCount={selectedAssets.length}
              onChange={(next) => {
                setMultiSelect(next);
                if (!next) setSelectedAssets([]);
              }}
              onClear={() => setSelectedAssets([])}
            />
          )}
        </div>
      }
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      notice={
        <>
          {/* Shown while searching too: the results are still scoped, and the
              rule does not stop applying just because the rail went away. */}
          {type !== undefined && <AssetScopeNotice type={type} />}

          {/*
            In the notice strip — the one full-width band between the search
            row and the columns. An earlier attempt put this inside the
            scrolling content column, where it rendered but was clipped out of
            view: in the DOM, invisible.

            Hint rather than a tour: the dialog's contents load asynchronously,
            so a tour started on open resolves its anchors against an empty
            shell. A hint has no timing to get wrong. Search/no-folder states
            cannot accept the advertised action; a user's first upload
            dismisses the hint for that user regardless of shared contents.
          */}
          {showDropzone && (
            <ContextualHint
              hintId={HintId.FileExplorerUpload}
              bodyKey={
                allowMultiSelect
                  ? "onboarding.hint.file_explorer_upload_bubbles"
                  : undefined
              }
              variant="banner"
              className="shrink-0"
            />
          )}
        </>
      }
      rail={!filter ? <FolderView /> : undefined}
      results={
        <AssetResults
          assets={assets}
          viewMode={viewMode}
          showDropzone={showDropzone}
          type={type}
          selectedIndex={selectedIndex}
          onSelectIndex={onSelectIndex}
          onConfirmIndex={onConfirmIndex}
          multiSelect={multiSelect}
          isAssetChecked={isAssetChecked}
          renamingAssetId={renamingAssetId}
          onRenameStart={(asset) => setRenamingAssetId(asset.id)}
          onRenameSubmit={(asset, name) => {
            renameAsset(asset, name);
            setRenamingAssetId(undefined);
          }}
          onRenameCancel={() => setRenamingAssetId(undefined)}
          listboxId={browseListboxId(DIALOG)}
          optionIdFor={(index) => browseOptionId(DIALOG, index)}
          onColumnsChange={setTileColumns}
          onRangeRendered={(columns, rowStart, rowStop) =>
            // Unscoped for the same reason the first fetch is.
            onRerender(columns, rowStart, rowStop, undefined, filter)
          }
        />
      }
      details={
        <AssetDetailsPane
          asset={isBlocked(highlighted) ? undefined : highlighted}
          folderName={filter ? undefined : selectedFolder?.name}
        />
      }
      legendExtras={
        multiSelect
          ? [{ keys: ["Space"], label: t("file_explorer_ui.legend_select") }]
          : []
      }
      // Mounted once, here, rather than per card: the dialog has to outlive
      // the cell that opened it. A view switch or a virtualised scroll
      // unmounts that cell, and with it anything it owns.
      overlay={
        <EditAssetDialog
          open={!!editingAssetId}
          onClose={() => dispatch(setEditingAsset(undefined))}
        />
      }
      primaryLabel={primaryLabel}
      primaryDisabled={primaryDisabled}
      onPrimaryAction={
        canPick
          ? () =>
              multiSelect
                ? confirmMulti()
                : highlighted && confirmSingle(highlighted)
          : undefined
      }
    />
  );
}
